import React, { useMemo, useCallback } from 'react';
import { Flex, Text, Button, Tag, Collapse, Icon } from 'core/components';
import styled from 'styled-components';
import { useHotkeys } from '@blueprintjs/core';
import CodeMirror from 'core/components/CodeMirror';

const ApprovalActionButton = styled(Button)`
  padding: 6px 10px;
  flex: 1;
  max-width: calc(50% - 4px);
`;

export const KaizenToolResultConfirmActions = ({ tool, onApprove, onApproveAndAutoApprove, onReject, disabled }) => {
  // Determine if auto-approve option should be shown based on tool configuration
  // by checking for an explicit allow_auto_approve we default to the most conservative option
  // of not showing the auto-approve button.
  const allowAutoApprove = tool.allow_auto_approve === true;

  const handleApprove = useCallback(() => {
    if (!disabled) {
      onApprove();
    }
  }, [disabled, onApprove]);

  const handleApproveAndAutoApprove = useCallback(() => {
    if (!disabled) {
      onApproveAndAutoApprove();
    }
  }, [disabled, onApproveAndAutoApprove]);

  const handleReject = useCallback(() => {
    if (!disabled) {
      onReject();
    }
  }, [disabled, onReject]);

  const [isDetailsOpen, setIsDetailsOpen] = React.useState(true);

  const toggleDetails = useCallback(() => {
    setIsDetailsOpen((prev) => !prev);
  }, []);

  // Memoize hotkeys array to avoid infinitely re-binding hotkeys
  // Conditionally include auto-approve hotkey based on allowAutoApprove
  const hotkeys = useMemo(() => {
    const baseHotkeys = [
      {
        combo: '1',
        label: 'Approve tool once',
        onKeyDown: handleApprove,
        preventDefault: true
      }
    ];

    if (allowAutoApprove) {
      baseHotkeys.push({
        combo: '2',
        label: 'Approve tool for conversation',
        onKeyDown: handleApproveAndAutoApprove,
        preventDefault: true
      });
    }

    baseHotkeys.push({
      combo: allowAutoApprove ? '3' : '2',
      label: 'Reject tool usage',
      onKeyDown: handleReject,
      preventDefault: true
    });

    return baseHotkeys;
  }, [handleApprove, handleApproveAndAutoApprove, handleReject, allowAutoApprove]);

  const { handleKeyDown, handleKeyUp } = useHotkeys(hotkeys);

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      gap={1}
      mt={1}
      flex={1}
      width="100%"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
    >
      <Text small>
        The <strong>{tool.tool_display_name || tool.tool_name}</strong> tool requires your approval before execution.
      </Text>
      <Flex gap={2} width="72%" flexWrap="wrap">
        <ApprovalActionButton
          intent="success"
          text={
            <Flex alignItems="center" gap={1}>
              <Tag round>1</Tag>
              Approve
            </Flex>
          }
          onClick={onApprove}
          disabled={disabled}
        />
        {allowAutoApprove && (
          <ApprovalActionButton
            intent="success"
            text={
              <Flex alignItems="center" gap={1}>
                <Tag round>2</Tag>
                Approve All
              </Flex>
            }
            onClick={onApproveAndAutoApprove}
            disabled={disabled}
          />
        )}
        <ApprovalActionButton
          intent="danger"
          text={
            <Flex alignItems="center" gap={1}>
              <Tag round>{allowAutoApprove ? '3' : '2'}</Tag>
              Reject
            </Flex>
          }
          onClick={onReject}
          disabled={disabled}
        />
      </Flex>
      <Flex alignItems="center" gap="4px" mt={1} style={{ cursor: 'pointer' }} onClick={toggleDetails}>
        <Icon icon={isDetailsOpen ? 'caret-down' : 'caret-right'} size={12} color="muted" />
        <Text small muted>
          {isDetailsOpen ? 'Hide' : 'View'} tool details
        </Text>
      </Flex>
      <Collapse isOpen={isDetailsOpen}>
        <CodeMirror
          mode="javascript"
          value={JSON.stringify(tool.query_object, null, 2)}
          options={{
            readOnly: true
          }}
        />
      </Collapse>
    </Flex>
  );
};
